import React, { useEffect, useRef, useState } from 'react';
import { supabase } from '../../services/supabase';
import { getDailyTip } from '../../utils/dailyTip';

const money = (cents) => new Intl.NumberFormat('en-US', {
  style: 'currency', currency: 'USD',
}).format((Number(cents) || 0) / 100);

const periodLabel = (payload) => {
  if (!payload?.period_start || !payload?.period_end) return 'Family digest';
  const zone = payload.time_zone || 'UTC';
  try {
    const date = new Intl.DateTimeFormat('en-US', {
      month: 'short', day: 'numeric', timeZone: zone,
    });
    return `${date.format(new Date(payload.period_start))} – ${date.format(
      new Date(new Date(payload.period_end).getTime() - 1),
    )}`;
  } catch {
    return 'Weekly digest';
  }
};

function ParentDigest({ payload }) {
  const children = Array.isArray(payload?.children) ? payload.children : [];
  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-600 dark:text-slate-300">
        Your family’s {payload?.kind === 'monthly' ? 'month' : 'week'}, {periodLabel(payload)}
      </p>
      {payload?.ai?.intro && (
        <p className="text-sm text-gray-700 dark:text-slate-200">{payload.ai.intro}</p>
      )}
      {children.length === 0 ? (
        <p className="text-sm text-gray-600 dark:text-slate-300">
          No child activity was recorded in this period.
        </p>
      ) : children.map((child, index) => (
        <section
          key={index}
          className="rounded-lg border border-gray-200 p-3 dark:border-slate-700"
        >
          <h3 className="font-semibold text-gray-900 dark:text-white">
            {child.first_name || 'Child'}
          </h3>
          {payload?.ai?.kids?.[index] && (
            <p className="mt-1 text-sm text-gray-700 dark:text-slate-200">
              {payload.ai.kids[index]}
            </p>
          )}
          <p className="mt-1 text-sm text-gray-700 dark:text-slate-200">
            {Number(child.completed_chores) || 0} chores completed ·
            {' '}{Number(child.goals_completed) || 0} goals completed
          </p>
          <p className="mt-1 text-sm text-gray-700 dark:text-slate-200">
            {money(child.income_cents)} received · {money(child.spent_cents)} spent ·
            {' '}{money(child.savings_added_cents)} added to savings
          </p>
          {Number(child.active_goals) > 0 && (
            <p className="mt-1 text-xs text-gray-600 dark:text-slate-300">
              {child.active_goals} active goals · {money(child.current_goal_savings_cents)} saved toward them
            </p>
          )}
        </section>
      ))}
    </div>
  );
}

export default function ParentNotificationBell({ parentId }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState('');
  const container = useRef(null);
  const [today, setToday] = useState(() => new Date());
  const [tipRead, setTipRead] = useState(false);
  const dailyTip = getDailyTip('parent', today);
  const tipKey = parentId && dailyTip
    ? `mypiggy:daily-tip:${parentId}:${dailyTip.dateKey}` : null;
  const tipItem = tipKey ? {
    id: tipKey,
    kind: 'daily_tip',
    title: "Today's Pocket Tip",
    body: `${dailyTip.categoryEmoji} ${dailyTip.title} — ${dailyTip.text}`,
    read_at: tipRead ? today.toISOString() : null,
  } : null;
  const unreadTotal = unread + (tipItem && !tipRead ? 1 : 0);

  useEffect(() => {
    const timer = window.setInterval(() => setToday(new Date()), 60 * 1000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!tipKey) return;
    try {
      setTipRead(window.localStorage.getItem(tipKey) === 'read');
    } catch {
      setTipRead(false);
    }
  }, [tipKey]);

  useEffect(() => {
    if (!parentId) return undefined;
    let active = true;
    const load = async () => {
      const [rows, count] = await Promise.all([
        supabase.from('parent_notifications').select(
          'id,kind,title,body,payload,created_at,read_at',
        ).eq('parent_id', parentId).order('created_at', { ascending: false }).limit(20),
        supabase.from('parent_notifications').select('id', {
          count: 'exact', head: true,
        }).eq('parent_id', parentId).is('read_at', null),
      ]);
      if (!active) return;
      if (rows.error || count.error) {
        setError('Could not load notifications. Please try again.');
      } else {
        setItems(rows.data || []);
        setUnread(count.count || 0);
        setError('');
      }
    };
    load();
    return () => { active = false; };
  }, [parentId, open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') { setOpen(false); setSelected(null); }
    };
    const onPointer = (event) => {
      if (!container.current?.contains(event.target)) {
        setOpen(false); setSelected(null);
      }
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPointer);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPointer);
    };
  }, [open]);

  const showItem = async (item) => {
    setSelected(item);
    if (item.kind === 'daily_tip') {
      if (!tipRead && tipKey) {
        try { window.localStorage.setItem(tipKey, 'read'); } catch { /* Storage is optional. */ }
        setTipRead(true);
      }
      return;
    }
    if (item.read_at) return;
    const { data, error: readError } = await supabase.rpc(
      'mark_my_parent_notification_read', { p_notification_id: item.id },
    );
    if (readError || !data) {
      setError('Could not mark this notification as read.');
      return;
    }
    const readAt = new Date().toISOString();
    setItems((current) => current.map((row) => (
      row.id === item.id ? { ...row, read_at: readAt } : row
    )));
    setUnread((current) => Math.max(0, current - 1));
  };

  return (
    <div className="relative shrink-0" ref={container}>
      <button
        type="button"
        aria-label={`Notifications${unreadTotal ? `, ${unreadTotal} unread` : ''}`}
        aria-expanded={open}
        aria-haspopup="dialog"
        data-tour="pocket-tip"
        title="Notifications"
        onClick={() => { setOpen((value) => !value); setSelected(null); }}
        className="relative flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 text-gray-700 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
      >
        <svg aria-hidden="true" width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9Z" />
          <path d="M10 21h4" />
        </svg>
        {unreadTotal > 0 && (
          <span className="absolute -right-1 -top-1 min-w-5 rounded-full bg-rose-600 px-1 text-center text-xs font-bold leading-5 text-white">
            {unreadTotal > 99 ? '99+' : unreadTotal}
          </span>
        )}
      </button>
      {open && (
        <div
          role="dialog"
          aria-label="Parent notifications"
          className="absolute right-0 z-40 mt-2 max-h-[min(70vh,36rem)] w-[min(23rem,calc(100vw-2rem))] overflow-y-auto rounded-xl border border-gray-200 bg-white p-4 text-left shadow-xl dark:border-slate-700 dark:bg-slate-900"
        >
          <div className="mb-3 flex items-center justify-between gap-3">
            {selected ? (
              <button
                type="button"
                onClick={() => setSelected(null)}
                className="text-sm font-semibold text-blue-700 dark:text-blue-300"
              >
                ← Notifications
              </button>
            ) : <h2 className="font-bold text-gray-900 dark:text-white">Notifications</h2>}
            <button
              type="button"
              aria-label="Close notifications"
              onClick={() => { setOpen(false); setSelected(null); }}
              className="rounded px-2 text-xl text-gray-600 hover:bg-gray-100 dark:text-slate-300 dark:hover:bg-slate-800"
            >×</button>
          </div>
          {error && <p role="alert" className="mb-2 text-sm text-red-700 dark:text-red-300">{error}</p>}
          {selected ? (
            <div>
              <h2 className="mb-2 font-bold text-gray-900 dark:text-white">{selected.title}</h2>
              {(selected.kind === 'weekly_digest' || selected.kind === 'monthly_digest')
                ? <ParentDigest payload={selected.payload} />
                : <p className="text-sm text-gray-700 dark:text-slate-200">{selected.body}</p>}
            </div>
          ) : (items.length || tipItem) ? (
            <ul className="space-y-2">
              {[...(tipItem ? [tipItem] : []), ...items].map((item) => (
                <li key={item.id}>
                  <button
                    type="button"
                    onClick={() => showItem(item)}
                    className="w-full rounded-lg border border-gray-200 p-3 text-left hover:bg-blue-50 dark:border-slate-700 dark:hover:bg-slate-800"
                  >
                    <span className="flex items-center gap-2 font-semibold text-gray-900 dark:text-white">
                      {!item.read_at && <span className="h-2 w-2 rounded-full bg-blue-600" aria-label="Unread" />}
                      {item.title}
                    </span>
                    <span className="mt-1 block text-xs text-gray-600 dark:text-slate-300">
                      {(item.kind === 'weekly_digest' || item.kind === 'monthly_digest')
                        ? periodLabel(item.payload) : item.body}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-gray-600 dark:text-slate-300">
              No notifications yet. Your daily Pocket Tip and family digests will appear here.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

